import { Box, Button, CircularProgress, Typography, styled, useTheme } from '@mui/material';
import Breadcrumbs from 'components/breadcrumbs';
import Dialog from 'components/dialog';
import Error from 'components/error';
import FilterSidebar from 'components/filter-sidebar';
import List from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';
import LoadingSpinner from 'components/loading-spinner';
import Paper from 'components/paper';
import { useAccountState } from 'contexts/account/account-context';
import { saveAs } from 'file-saver';
import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';
import Mui from 'materials';
import React from 'react';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { FileSummary } from 'views/sea-cloud-file-upload/types/file-summary.type';
import { ImportStatus } from 'views/sea-cloud-file-upload/types/import-status.type';
import { postSeaCloudFileImportStatus, postSeaCloudFileUpload } from './api/create-sea-cloud-file';
import { getSeaCloudFileDownload, getSeaCloudFileSummary } from './api/get-sea-cloud-file';
import SeaCloudFileUploadTabsHeader from './components/sea-cloud-file-upload-tabs-header';

const ButtonBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  '&>:nth-of-type(1)': {
    marginRight: theme.spacing(4),
  },
  '&>:nth-of-type(2)': {
    margin: theme.spacing(4, 0),
  },

  [theme.breakpoints.down('sm')]: {
    width: '100%',
    flexDirection: 'column',
    '&>:nth-of-type(2)': {
      margin: theme.spacing(0, 0),
    },
  },
}));

const TextWrapper = styled(Typography)(({ theme }) => ({
  wordWrap: 'break-word',
  maxWidth: 200,

  [theme.breakpoints.up('md')]: {
    maxWidth: 2000,
  },
}));

type Props = {};

const ViewFileSummary: React.FC<Props> = () => {
  const { account } = useAccountState();
  const queryClient = useQueryClient();
  const intl = useIntl();
  const theme = useTheme();

  // #region FETCH DATA

  const {
    data: file,
    error,
    isLoading,
    isError,
  } = useGet(['getSeaCloudFileSummary', account.company?.companyId], ({ queryKey }) => getSeaCloudFileSummary(String(queryKey[1])), {
    enabled: !!account.company?.companyId,
  });

  const downloadFileQuery = useGet(
    ['getSeaCloudFileDownload', account.company?.companyId, file?.fileUploadId],
    ({ queryKey }) => getSeaCloudFileDownload(String(queryKey[1]), Number(queryKey[2])),
    {
      enabled: false,
    }
  );

  const handleDownloadFile = async () => {
    const result = await downloadFileQuery.refetch();
    if (!result.isSuccess || !result.data) return;

    const response = result.data;
    const disposition = response.headers['content-disposition']?.split(';');
    const fn = disposition?.find((n: string) => n.includes('filename='));
    if (fn) {
      const filename = fn.replace('filename=', '').replace(/"/gi, '').trim();
      saveAs(response.data, filename, { autoBom: false });
    }
  };

  // #endregion FETCH DATA

  // #region POST DATA

  const importStatus = usePost<ImportStatus, boolean>(
    (request) => postSeaCloudFileImportStatus(account.company?.companyId ?? '', file?.fileUploadId ?? 0, request),
    () => {
      queryClient.invalidateQueries({
        queryKey: ['getSeaCloudFileSummary']
      });
    },
    (error) => {
      console.log(error);
    }
  );

  const uploadFile = usePost<FileSummary, { globalCompanyId: string; fileName: string; file: File }>(
    (request) => postSeaCloudFileUpload(request.globalCompanyId, request.fileName, request.file),
    (response) => {
      queryClient.setQueryData(['getSeaCloudFileSummary', account.company?.companyId], response);
    },
    (error) => {
      console.log(error);
    }
  );

  // #endregion POST DATA

  // #region HANDLERS

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files ? event.target.files[0] : null;
    if (file) {
      uploadFile.mutate({
        globalCompanyId: account.company!.companyId,
        fileName: file.name,
        file: file,
      });
    }
  };

  // #endregion HANDLERS

  return isError ? (
    <Error error={error} />
  ) : !file || isLoading ? (
    <LoadingSpinner />
  ) : (
    <>
      {uploadFile.isPending ? (
        <Paper>{intl.formatMessage({ id: `general.loading` })}</Paper>
      ) : (
        <>
          <SeaCloudFileUploadTabsHeader utils={<Breadcrumbs />} />
          <Paper transparent>
            <FilterSidebar>
              {!!file.sourceFileName && (
                <Button
                  fullWidth
                  disabled={downloadFileQuery.isFetching}
                  onClick={() => handleDownloadFile()}
                  startIcon={downloadFileQuery.isFetching ? <CircularProgress color="inherit" size={18} /> : null}>
                  <FormattedMessage id="form.buttons.downloadFile" />
                </Button>
              )}
              {!!file.sourceFileName ? (
                <ButtonBox>
                  <Dialog
                    maxWidth="xs"
                    title="Are you sure?" //todo: translate
                    button={
                      <Button fullWidth sx={{ margin: theme.spacing(4, 0) }}>
                        <FormattedMessage id="form.buttons.cancel" />
                      </Button>
                    }>
                    <Box display={'flex'} justifyContent={'end'}>
                      <Button type="submit" size="small" onClick={() => importStatus.mutate(false)}>
                        <FormattedMessage id="form.buttons.cancel" />
                      </Button>
                    </Box>
                  </Dialog>
                  <Dialog
                    maxWidth="xs"
                    title="Are you sure?"
                    button={
                      <Button fullWidth>
                        <FormattedMessage id="form.buttons.confirm" />
                      </Button>
                    }>
                    <Box display={'flex'} justifyContent={'end'}>
                      <Button type="submit" size="small" onClick={() => importStatus.mutate(true)}>
                        <FormattedMessage id="form.buttons.confirm" />
                      </Button>
                    </Box>
                  </Dialog>
                </ButtonBox>
              ) : (
                <>
                  <input type="file" name="fileinput" id="fileinput" onChange={handleFileUpload} style={{ display: 'none' }} />
                  <Mui.Button fullWidth component="label" htmlFor="fileinput" size="small">
                    <FormattedMessage id="form.buttons.chooseFile" />
                  </Mui.Button>
                </>
              )}
            </FilterSidebar>
            {!!file.sourceFileName && (
              <>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={5}>
                  <Typography variant="h3" color={theme.palette.common.white}>
                    <FormattedMessage id="form.fileUpload.heading" />
                  </Typography>
                </Box>

                <List
                  options={[
                    { id: 'field', label: intl.formatMessage({ id: 'form.fileUpload.options.field' }) },
                    { id: 'value', label: intl.formatMessage({ id: 'form.fileUpload.options.value' }) },
                  ]}>
                  <ListRow>
                    <ListCell align="left">
                      <FormattedMessage id="form.fileUpload.listRow.fileName" />
                    </ListCell>
                    <ListCell align="left">
                      <TextWrapper>{file.sourceFileName}</TextWrapper>
                    </ListCell>
                  </ListRow>
                  <ListRow>
                    <ListCell align="left">
                      <FormattedMessage id="form.fileUpload.listRow.source" />
                    </ListCell>
                    <ListCell align="left">{file.exportSource}</ListCell>
                  </ListRow>
                  <ListRow>
                    <ListCell align="left">
                      <FormattedMessage id="form.fileUpload.listRow.destination" />
                    </ListCell>
                    <ListCell align="left">{file.exportDestination}</ListCell>
                  </ListRow>
                  <ListRow>
                    <ListCell align="left">
                      <FormattedMessage id="form.fileUpload.listRow.version" />
                    </ListCell>
                    <ListCell align="left">{file.exportVersion}</ListCell>
                  </ListRow>
                  <ListRow>
                    <ListCell align="left">
                      <FormattedMessage id="form.fileUpload.listRow.exportDate" />
                    </ListCell>
                    <ListCell align="left">
                      <FormattedDate value={file.exportDate} day="2-digit" month="short" year="numeric" />
                    </ListCell>
                  </ListRow>
                  <ListRow>
                    <ListCell align="left">
                      <FormattedMessage id="form.fileUpload.listRow.periodStart" />
                    </ListCell>
                    <ListCell align="left">
                      <FormattedDate value={file.startDate} day="2-digit" month="short" year="numeric" />
                    </ListCell>
                  </ListRow>
                  <ListRow>
                    <ListCell align="left">
                      <FormattedMessage id="form.fileUpload.listRow.periodEnd" />
                    </ListCell>
                    <ListCell align="left">
                      <FormattedDate value={file.endDate} day="2-digit" month="short" year="numeric" />
                    </ListCell>
                  </ListRow>
                </List>
                {file.companies.map((company, idx) => (
                  <React.Fragment key={idx}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={5} mt={8}>
                      <Typography variant="h3" color={theme.palette.common.white}>
                        {company.name}
                      </Typography>
                    </Box>
                    <Box mb={4}>
                      <List
                        options={[
                          { id: 'name', label: intl.formatMessage({ id: 'form.fileUpload.options.siteName' }) },
                          { id: 'lastDateOfRegistration', label: intl.formatMessage({ id: 'form.fileUpload.options.lastDate' }) },
                          { id: 'latitude', label: intl.formatMessage({ id: 'form.fileUpload.options.latitude' }) },
                          { id: 'longitude', label: intl.formatMessage({ id: 'form.fileUpload.options.longitude' }) },
                        ]}>
                        {company.sites.map((site) => (
                          <ListRow key={site.id}>
                            <ListCell align="left">{site.name}</ListCell>
                            <ListCell align="left">
                              <FormattedDate value={site.lastDateOfRegistration} day="2-digit" month="short" year="numeric" />
                            </ListCell>
                            <ListCell align="left">{site.latitude}</ListCell>
                            <ListCell align="left">{site.longitude}</ListCell>
                          </ListRow>
                        ))}
                      </List>
                    </Box>
                  </React.Fragment>
                ))}
              </>
            )}
            {!!file.errorMessage && (
              <>
                <Typography variant="h3" color={theme.palette.common.white}>
                  <FormattedMessage id="form.errors" />
                </Typography>
                <Paper>{file.errorMessage}</Paper>
              </>
            )}
          </Paper>
        </>
      )}
    </>
  );
};

export default ViewFileSummary;
